import { EChartsInstance } from 'echarts-for-react';
import { SeriesData } from 'echarts/types/dist/shared';
import { isEmpty, sortBy } from 'lodash/fp';

import _ from '../../../locale';
import { LOCATION_ID_KEY, VALUE_KEY } from '../../../models/data-key';
import { DataResponse } from '../../../types';
import formatChartValue from '../../formatting/formatChartValue';
import { measureTextSize } from '../text-utils';

export const TOP_LOCATION_LIST_WIDTH: number = 200;
export const TOP_LOCATION_LIST_ITEMS_AMOUNT: number = 10;
const NAME_OFFSET: number = 20;
const NAME_WIDTH: number = TOP_LOCATION_LIST_WIDTH - NAME_OFFSET;

export default (dataResponse: DataResponse, theme, isHidden: boolean = false) =>
    (instance: EChartsInstance) => {
        const { textStyle } = theme;

        let heightCounter = 30;
        const z = 5;

        const title = {
            type: 'text',
            z,
            silent: true,
            style: {
                ...textStyle,
                fontWeight: 500,
                text: _('map_chart_top_list_title'),
                lineHeight: 20,
            },
            tooltip: { show: false },
        };

        const { columns, records } = dataResponse;

        const locationIdx = columns.indexOf(LOCATION_ID_KEY);
        const valueIdx = columns.indexOf(VALUE_KEY);

        const topRecords = sortBy((record) => -Number(record[valueIdx]), records);

        const colorsMap = {};
        const series = instance?._model?._componentsMap?.data?.get?.('series')?.[0];
        if (series) {
            const data: SeriesData = Object.entries(series).filter(
                ([key, value]) =>
                    key.startsWith('__ec_inner') &&
                    value?.hasOwnProperty('data') &&
                    value?.hasOwnProperty('dataBeforeProcessed')
            )?.[0]?.[1]?.data as SeriesData;

            if (data) {
                const { _idList, _itemVisuals } = data;
                _idList.forEach((id, idx) => {
                    colorsMap[id] = _itemVisuals[idx]?.style?.fill;
                });
            }
        }

        const list = Array(TOP_LOCATION_LIST_ITEMS_AMOUNT)
            .fill()
            .map((v, idx) => {
                let locationId = -1;
                let label = '';
                let height = 0;
                let color = null;

                if (!isEmpty(topRecords?.[idx])) {
                    const record = topRecords[idx];
                    locationId = record[locationIdx].toString();
                    const name = _(`location_${locationId}`);
                    label = `${name} - ${formatChartValue(record[valueIdx])}`;
                    height = measureTextSize(name, textStyle, NAME_WIDTH).height + 14;
                    color = colorsMap[locationId] || 'gray';
                }

                const group = {
                    type: 'group',
                    id: idx,
                    z,
                    tooltip: { show: false },
                    y: heightCounter,
                    x: 0,
                    children: [
                        {
                            type: 'circle',
                            id: `${idx}-circle`,
                            name: locationId,
                            z,
                            x: 3,
                            y: height / 2,
                            shape: { r: 3 },
                            style: { fill: color },
                            tooltip: { show: false },
                        },
                        {
                            type: 'text',
                            id: `${idx}-name`,
                            name: locationId,
                            z,
                            x: NAME_OFFSET,
                            y: 7,
                            style: {
                                ...theme.textStyle,
                                text: label,
                                width: TOP_LOCATION_LIST_WIDTH - NAME_OFFSET,
                                overflow: 'break',
                            },
                            tooltip: { show: false },
                        },
                    ],
                };

                heightCounter += height;
                return group;
            });

        const isGeneratingPDF = !!instance?.getOption()?.isGeneratingPDF;

        const background = isGeneratingPDF
            ? null
            : {
                  type: 'rect',
                  z,
                  silent: true,
                  shape: {
                      width: TOP_LOCATION_LIST_WIDTH,
                      height: heightCounter + 20,
                  },
                  style: { fill: '#fff' },
              };

        return [
            {
                type: 'group',
                id: 'top-list',
                z,
                show: false,
                ...(isHidden
                    ? { x: -window.innerWidth - TOP_LOCATION_LIST_WIDTH }
                    : isGeneratingPDF
                      ? { x: 0, bottom: 0, left: 0 }
                      : { x: 0, top: 60, right: 20 }),
                pointer: 'default',
                tooltip: { show: false },
                children: [
                    background,
                    {
                        type: 'group',
                        z,
                        x: isHidden ? -window.innerWidth : 10,
                        y: 10,
                        tooltip: { show: false },
                        children: [title, ...list],
                    },
                ],
            },
        ];
    };
